import { cn } from '@iheart/ui';

/**
 * Shimmer skeleton for route-level loading states.
 *
 * Shows a layout-matching skeleton with:
 * - A header bar placeholder
 * - Stat card placeholders (5 across)
 * - Two content block placeholders
 *
 * This gives users a sense of structure loading rather than
 * a blank screen with a tiny spinner.
 */
export function PageSkeleton() {
  return (
    <div className="flex min-h-full flex-col animate-pulse">
      {/* Header skeleton */}
      <div className="border-b border-neutral-100 px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-6 w-48 rounded-lg bg-neutral-100" />
            <div className="mt-2 h-4 w-64 rounded-md bg-neutral-100" />
          </div>
          <div className="flex items-center gap-2">
            <div className="h-9 w-24 rounded-lg bg-neutral-100" />
            <div className="h-9 w-28 rounded-lg bg-neutral-100" />
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-6 p-6">
        {/* Stats row skeleton */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonCard key={i} className="h-20" />
          ))}
        </div>

        {/* KPI row skeleton */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} className="h-24" />
          ))}
        </div>

        {/* Two-column content skeleton */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SkeletonCard className="h-64" />
          </div>
          <div>
            <SkeletonCard className="h-64" />
          </div>
        </div>
      </div>
    </div>
  );
}

function SkeletonCard({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'rounded-xl border border-neutral-100 bg-neutral-50',
        className,
      )}
    />
  );
}

/**
 * Compact loading indicator — used as the Suspense fallback for
 * routes that don't need a full-page skeleton (e.g. lightweight pages).
 */
export function RouteSpinner() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="flex flex-col items-center gap-2">
        <div className="size-6 animate-spin rounded-full border-2 border-pink-200 border-t-pink-500" />
        <span className="text-xs text-neutral-400">Loading&hellip;</span>
      </div>
    </div>
  );
}
